import { useState, useEffect, useCallback } from 'react';
import { useUrlState } from '../hooks/useUrlParams';
import { contactSubmissionAPI } from '../services/api';

const styles = {
  container: { width: '100%' },
  header: { marginBottom: '20px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 },
  subtitle: { fontSize: '13px', color: '#64748b', marginTop: '2px' },
  card: { backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderBottom: '1px solid #e2e8f0', gap: '12px', flexWrap: 'wrap' },
  searchInput: { flex: 1, maxWidth: '320px', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none' },
  totalChip: { fontSize: '13px', color: '#64748b', padding: '6px 12px', backgroundColor: '#f1f5f9', borderRadius: '8px', fontWeight: '500' },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '900px' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
  thRight: { textAlign: 'right' },
  td: { padding: '14px 16px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#1e293b', verticalAlign: 'top' },
  tdMuted: { padding: '14px 16px', borderBottom: '1px solid #f1f5f9', fontSize: '13px', color: '#64748b', verticalAlign: 'top', whiteSpace: 'nowrap' },
  tdRight: { padding: '14px 16px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', verticalAlign: 'top' },
  nameCell: { fontWeight: '600', color: '#1e293b' },
  emailLink: { color: '#4f46e5', textDecoration: 'none', fontWeight: '500' },
  phoneLink: { color: '#4f46e5', textDecoration: 'none' },
  subjectText: { fontWeight: '500', color: '#1e293b' },
  messageSnippet: { color: '#64748b', maxWidth: '380px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' },
  viewButton: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#fff', color: '#475569', fontSize: '13px', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.12s' },
  emptyState: { textAlign: 'center', padding: '80px 20px', color: '#64748b' },
  loadingState: { textAlign: 'center', padding: '80px 20px', color: '#64748b' },
  errorBox: { padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', margin: '16px', fontSize: '14px' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: '12px' },
  pageInfo: { fontSize: '13px', color: '#64748b' },
  pageButtons: { display: 'flex', gap: '4px' },
  pageButton: { padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#fff', cursor: 'pointer', fontSize: '13px', color: '#475569' },
  pageButtonDisabled: { opacity: 0.5, cursor: 'not-allowed' },

  // Modal
  modalBackdrop: { position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' },
  modal: { backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 60px rgba(0,0,0,0.25)' },
  modalHeader: { padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' },
  modalTitle: { fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: 0, lineHeight: '1.4', wordBreak: 'break-word' },
  modalSubtitle: { fontSize: '12px', color: '#94a3b8', marginTop: '4px' },
  closeButton: { width: '32px', height: '32px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: '#64748b', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  modalBody: { padding: '20px 24px', overflowY: 'auto', flex: 1 },
  metaGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '20px' },
  metaItem: {},
  metaLabel: { fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' },
  metaValue: { fontSize: '14px', color: '#1e293b', fontWeight: '500', wordBreak: 'break-word' },
  metaLink: { color: '#4f46e5', textDecoration: 'none', fontWeight: '500', wordBreak: 'break-word' },
  messageBlock: { padding: '16px 18px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' },
  messageLabel: { fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' },
  messageText: { whiteSpace: 'pre-wrap', fontSize: '14px', lineHeight: '1.65', color: '#334155', margin: 0 },
  modalFooter: { padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px', backgroundColor: '#fafbfc' },
  secondaryButton: { padding: '9px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', border: '1px solid #e2e8f0', backgroundColor: '#fff', color: '#475569' },
  primaryButton: { padding: '9px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', border: 'none', backgroundColor: '#4f46e5', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '6px', textDecoration: 'none' },
};

function formatDateTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return iso; }
}

const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ContactSubmissions = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useUrlState('page', 1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [viewing, setViewing] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = { page, limit: 20 };
      if (search) params.search = search;
      const response = await contactSubmissionAPI.getAll(params);
      const data = response.data || {};
      setItems(Array.isArray(data.data) ? data.data : []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to fetch contact submissions');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Debounce search input → committed search after 350ms idle.
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput);
        setPage(1);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput, search, setPage]);

  // Close modal on Escape
  useEffect(() => {
    if (!viewing) return;
    const onKey = (e) => { if (e.key === 'Escape') setViewing(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [viewing]);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Contact Submissions</h1>
        <p style={styles.subtitle}>Messages submitted via the public Contact Us page.</p>
      </div>

      <div style={styles.card}>
        <div style={styles.toolbar}>
          <input
            type="text"
            placeholder="Search by name, email, subject, or message..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={styles.searchInput}
          />
          <span style={styles.totalChip}>{total} {total === 1 ? 'submission' : 'submissions'}</span>
        </div>

        {error && <div style={styles.errorBox}>{error}</div>}

        {loading ? (
          <div style={styles.loadingState}>Loading...</div>
        ) : items.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={{ fontSize: '15px', margin: 0 }}>No contact submissions yet.</p>
            <p style={{ fontSize: '13px', marginTop: '6px' }}>Submissions from the public /contact page will appear here.</p>
          </div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Received</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Phone</th>
                  <th style={styles.th}>Subject & Message</th>
                  <th style={{ ...styles.th, ...styles.thRight }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td style={styles.tdMuted}>{formatDateTime(item.createdAt)}</td>
                    <td style={{ ...styles.td, ...styles.nameCell }}>{item.name}</td>
                    <td style={styles.td}>
                      <a href={`mailto:${item.email}`} style={styles.emailLink}>{item.email}</a>
                    </td>
                    <td style={styles.td}>
                      {item.phone ? (
                        <a href={`tel:${item.phone}`} style={styles.phoneLink}>{item.phone}</a>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      <div style={styles.subjectText}>{item.subject}</div>
                      <div style={styles.messageSnippet}>{item.message}</div>
                    </td>
                    <td style={styles.tdRight}>
                      <button
                        type="button"
                        onClick={() => setViewing(item)}
                        style={styles.viewButton}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#eef2ff';
                          e.currentTarget.style.borderColor = '#c7d2fe';
                          e.currentTarget.style.color = '#4f46e5';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#fff';
                          e.currentTarget.style.borderColor = '#e2e8f0';
                          e.currentTarget.style.color = '#475569';
                        }}
                      >
                        <EyeIcon />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && items.length > 0 && totalPages > 1 && (
          <div style={styles.pagination}>
            <span style={styles.pageInfo}>Page {page} of {totalPages}</span>
            <div style={styles.pageButtons}>
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                style={{ ...styles.pageButton, ...(page <= 1 ? styles.pageButtonDisabled : {}) }}
              >Prev</button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                style={{ ...styles.pageButton, ...(page >= totalPages ? styles.pageButtonDisabled : {}) }}
              >Next</button>
            </div>
          </div>
        )}
      </div>

      {viewing && (
        <div style={styles.modalBackdrop} onClick={() => setViewing(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 style={styles.modalTitle}>{viewing.subject}</h2>
                <div style={styles.modalSubtitle}>Received {formatDateTime(viewing.createdAt)}</div>
              </div>
              <button type="button" onClick={() => setViewing(null)} style={styles.closeButton} aria-label="Close">
                <CloseIcon />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.metaGrid}>
                <div style={styles.metaItem}>
                  <div style={styles.metaLabel}>Name</div>
                  <div style={styles.metaValue}>{viewing.name}</div>
                </div>
                <div style={styles.metaItem}>
                  <div style={styles.metaLabel}>Phone</div>
                  <div style={styles.metaValue}>
                    {viewing.phone ? (
                      <a href={`tel:${viewing.phone}`} style={styles.metaLink}>{viewing.phone}</a>
                    ) : (
                      <span style={{ color: '#94a3b8' }}>—</span>
                    )}
                  </div>
                </div>
                <div style={{ ...styles.metaItem, gridColumn: '1 / -1' }}>
                  <div style={styles.metaLabel}>Email</div>
                  <div style={styles.metaValue}>
                    <a href={`mailto:${viewing.email}`} style={styles.metaLink}>{viewing.email}</a>
                  </div>
                </div>
              </div>

              <div style={styles.messageBlock}>
                <div style={styles.messageLabel}>Message</div>
                <p style={styles.messageText}>{viewing.message}</p>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button type="button" onClick={() => setViewing(null)} style={styles.secondaryButton}>Close</button>
              <a
                href={`mailto:${viewing.email}?subject=${encodeURIComponent('Re: ' + viewing.subject)}`}
                style={styles.primaryButton}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 17 4 12 9 7" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" />
                </svg>
                Reply via Email
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactSubmissions;
